import { yupResolver } from '@hookform/resolvers/yup';
import React, { useContext, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CancelEditButton } from '@/modules/AiAgents/components/Agents/Nugz/Buttons/CancelEditButton';
import { EditButton } from '@/modules/AiAgents/components/Agents/Nugz/Buttons/EditButton';
import { SaveButton } from '@/modules/AiAgents/components/Agents/Nugz/Buttons/SaveButton';
import { SubmitButton } from '@/modules/AiAgents/components/Agents/Nugz/Buttons/SubmitButton';
import { AIFieldsTable } from '@/modules/AiAgents/components/Agents/Nugz/Fields/AIFieldsTable';
import useAIChatBot from '@/modules/AiAgents/components/Agents/Nugz/logic';
import { RessetDataModal } from '@/modules/AiAgents/components/Agents/Nugz/Modals/RessetDataModal';
import { AIContext } from '@/modules/AiAgents/context/nugzContext/context';
import { AIActionsTypes } from '@/modules/AiAgents/context/nugzContext/reducer';
import { IAI } from '@/modules/AiAgents/types';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

import DocumentIcon from '~/images/shared/Document.svg';
import SquareIcon from '~/images/shared/Square.svg';

const AIFieldsTableContainer = () => {
  const { openModal } = useModal();
  const [selectedAsset] = useStore((store) => store?.asset);
  const { t } = useTranslation(['common', 'onboarding']);
  const {
    dispatch,
    state: { tableList, isEditing, areaUnit },
  } = useContext(AIContext);
  const { fieldsTableValidationSchema } = useAIChatBot();
  const methods = useForm<IAI.IFieldsFormSchema>({
    resolver: yupResolver(fieldsTableValidationSchema),
    mode: 'all',
    criteriaMode: 'firstError',
    shouldFocusError: true,
    defaultValues: {
      fields: tableList,
    },
  });
  const handleResetData = () => {
    dispatch({
      type: AIActionsTypes.setTableList,
      payload: [],
    });
    dispatch({
      type: AIActionsTypes.setIsEditing,
      payload: false,
    });
    dispatch({
      type: AIActionsTypes.setIsLoading,
      payload: false,
    });
    dispatch({
      type: AIActionsTypes.setMessagesList,
      payload: [
        {
          senderName: t('nugz'),
          text: t('ai-starter-message'),
          timestamp: new Date(),
          type: 'receiver',
        },
      ],
    });
    methods?.reset({ fields: [] });
  };
  // Update form fields when tableList changes
  useEffect(() => {
    methods.reset({ fields: tableList });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableList]);

  return (
    <FormProvider {...methods}>
      <div
        className='flex h-full  w-full flex-col overflow-auto rounded-xl bg-background-neutral-99 py-16'
        id='nugz-table-container'
      >
        <div className='flex items-center justify-between gap-16 p-16'>
          <div className='flex items-center gap-2'>
            <span className='text-24 text-primary-default'>
              <SquareIcon />
            </span>
            <span className='text-18 font-semi-bold'>
              {t('add-your-fields', { ns: 'onboarding' })}
            </span>
          </div>
          {tableList.length && !isEditing ? (
            <div className='flex items-center gap-16'>
              <div
                className='cursor-pointer text-base font-semi-bold text-error-400'
                onClick={() =>
                  openModal(
                    <RessetDataModal handleResetData={handleResetData} />,
                    {
                      containerClassName: 'max-w-md',
                    }
                  )
                }
              >
                {t('reset-data')}
              </div>
              <EditButton />
            </div>
          ) : isEditing ? (
            <div className='flex items-center gap-16'>
              <CancelEditButton />
              <SaveButton />
            </div>
          ) : (
            ''
          )}
        </div>
        {tableList.length ? (
          <div className='flex h-full w-full flex-col'>
            <div className='border-b-xs border-t-xs border-b-neutralVariant-90 border-t-neutralVariant-90 px-16 py-16 text-14 font-semi-bold text-primary-default'>
              {t('area-unit-defualt-description', {
                propertyName: selectedAsset?.name,
                areaUnit: areaUnit,
              })}
            </div>
            <form
              className='flex h-full flex-col justify-between'
              onSubmit={(e) => e.preventDefault()}
            >
              <AIFieldsTable />
              <SubmitButton />
            </form>
          </div>
        ) : (
          <div className='flex h-full w-full flex-col justify-center gap-16 border-t-xs border-t-neutralVariant-90 text-center font-bold'>
            <span className='self-center text-48 text-primary-60'>
              <DocumentIcon />
            </span>
            <span className='mx-auto w-fit max-w-xs text-18 text-text-default'>
              {t('chat-with-ai')}
            </span>
          </div>
        )}
      </div>
    </FormProvider>
  );
};

export { AIFieldsTableContainer };
